import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import SystemUpdateAltOutlinedIcon from '@mui/icons-material/SystemUpdateAltOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { PATHS } from '@app/routes/paths';
import type { PanelRoute, FullScreenRoute } from '@app/routes/types';
import { useLayoutStore } from '@shared/hooks/useLayoutStore';

interface NavItem {
  label: string;
  icon: React.ReactNode;
  path: string;
  route: PanelRoute | FullScreenRoute;
  type: 'panel' | 'fullscreen';
}

const topItems: NavItem[] = [
  {
    label: 'Routes',
    icon: <RouteOutlinedIcon />,
    path: PATHS.ROUTES,
    route: 'routes',
    type: 'panel',
  },
  {
    label: 'Holdings',
    icon: <FolderOutlinedIcon />,
    path: PATHS.HOLDINGS,
    route: 'holdings',
    type: 'panel',
  },
  {
    label: 'Catalog',
    icon: <SearchOutlinedIcon />,
    path: PATHS.CATALOG,
    route: 'catalog',
    type: 'panel',
  },
  {
    label: 'Orders',
    icon: <ShoppingCartOutlinedIcon />,
    path: PATHS.ORDERS,
    route: 'orders',
    type: 'panel',
  },
  {
    label: 'Updates',
    icon: <SystemUpdateAltOutlinedIcon />,
    path: PATHS.UPDATES,
    route: 'updates',
    type: 'fullscreen',
  },
  {
    label: 'Transfers',
    icon: <SendOutlinedIcon />,
    path: PATHS.TRANSFERS,
    route: 'transfers',
    type: 'fullscreen',
  },
];

const bottomItems: NavItem[] = [
  {
    label: 'Status',
    icon: <InfoOutlinedIcon />,
    path: PATHS.STATUS,
    route: 'status',
    type: 'panel',
  },
  {
    label: 'Support',
    icon: <SupportAgentOutlinedIcon />,
    path: PATHS.SUPPORT,
    route: 'support',
    type: 'panel',
  },
  {
    label: 'Activity',
    icon: <HistoryOutlinedIcon />,
    path: PATHS.ACTIVITY,
    route: 'activity',
    type: 'fullscreen',
  },
  {
    label: 'Settings',
    icon: <SettingsOutlinedIcon />,
    path: PATHS.SETTINGS,
    route: 'settings',
    type: 'fullscreen',
  },
];

const SIDENAV_WIDTH = 56;

export { SIDENAV_WIDTH };

export default function SideNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const setActivePanel = useLayoutStore((s) => s.setActivePanel);

  const handleClick = (item: NavItem) => {
    if (item.type === 'panel') {
      setActivePanel(item.route as PanelRoute);
      navigate(item.path);
    } else {
      setActivePanel(null);
      navigate(item.path);
    }
  };

  const isActive = (item: NavItem) => location.pathname.startsWith(item.path);

  const renderItem = (item: NavItem) => (
    <Tooltip key={item.route} title={item.label} placement="right">
      <IconButton
        onClick={() => handleClick(item)}
        sx={{
          borderRadius: 1,
          color: isActive(item) ? 'primary.main' : 'text.secondary',
          bgcolor: isActive(item) ? 'action.selected' : 'transparent',
          '&:hover': { bgcolor: 'action.hover' },
        }}
        aria-label={item.label}
      >
        {item.icon}
      </IconButton>
    </Tooltip>
  );

  return (
    <Box
      component="nav"
      data-testid="sidenav"
      sx={{
        width: SIDENAV_WIDTH,
        minWidth: SIDENAV_WIDTH,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 1,
        gap: 0.5,
        borderRight: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
        {topItems.map(renderItem)}
      </Box>
      <Divider flexItem />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {bottomItems.map(renderItem)}
      </Box>
    </Box>
  );
}
